import {
  Box,
  Card,
  Chip,
  Grid,
  LinearProgress,
  Skeleton,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import "../../Switcher.scss";
import CarrierMetricsChart from "../chart/CarrierMetricsChart";
import ServerStatsCombined from "../chart/ServerStatsCombined";
import TopCarriersWidget from "../chart/TopCarriersWidget";
import IvrAuthCard from "./IvrAuthCard";
import { getDashboardChart } from "../../redux/actions/adminPortal_dashboardAction";
import { useDispatch } from "react-redux";
import { api } from "../../mockData";
import CallReceivedIcon from "@mui/icons-material/CallReceived";
import axios from "axios";
import CallEnd from "@mui/icons-material/CallEnd";
import CallMissed from "@mui/icons-material/CallMissed";
import MemoryIcon from "@mui/icons-material/Memory";
import StorageIcon from "@mui/icons-material/Storage";
import DnsIcon from "@mui/icons-material/Dns";
import ErrorIcon from "@mui/icons-material/Error";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CallIcon from "@mui/icons-material/Call";

const drawerWidth = 240;
function AdminDashboard({ colorThem }) {
  const token = JSON.parse(localStorage.getItem("admin"));
  const [callsSummary, setCallsSummary] = useState({});
  const [loader, setLoader] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getDashboardChart());
  }, [dispatch]);

  useEffect(() => {
    let isMounted = true; // 🔒 prevent state update after unmount

    const fetchData = async () => {
      setLoader(true);
      // /api/serverstats removed from this auto-fetch — psutil's CPU
      // sampling blocks ~1s per call, so it's now opt-in via the
      // ServerStatsCombined component's button instead of every mount.
      try {
        const response = await axios.request({
          method: "get",
          maxBodyLength: Infinity,
          url: `${api.dev}/api/admin_calls_summary`,
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token.access_token} `,
          },
        });
        if (isMounted) setCallsSummary(response.data);
      } catch (error) {
        console.error("Call Summary API failed", error);
        if (isMounted) setCallsSummary(null);
      } finally {
        if (isMounted) setLoader(false);
      }
    };
    fetchData();

    return () => {
      isMounted = false; // cleanup
    };
  }, [token.access_token]);

  const cardStyle = (border) => ({
    position: "relative",
    borderRadius: "20px",
    p: 3,
    background: "#ffffffcc",
    backdropFilter: "blur(10px)",
    boxShadow: "0 10px 25px rgba(0, 0, 0, 0.1)",
    transition: "all 0.3s ease",
    cursor: "pointer",
    "&:hover": {
      transform: "translateY(-5px) scale(1.02)",
      boxShadow: "0 15px 35px rgba(0, 0, 0, 0.2)",
    },
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      borderRadius: "20px",
      padding: "2px",
      background: border,
      WebkitMask:
        "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
      WebkitMaskComposite: "xor",
      maskComposite: "exclude",
      pointerEvents: "none",
    },
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  });

  // ✅ Reusable Icon Container Style
  const iconContainerStyle = (bg) => ({
    background: bg,
    borderRadius: "50%",
    width: 70,
    height: 70,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: `0px 0px 15px ${
      bg.replace("linear-gradient(135deg,", "").replace(")", "").split(",")[0]
    }`,
    color: "#fff",
    transform: "scale(1)",
    transition: "transform 0.3s ease-in-out",
    "&:hover": {
      transform: "scale(1.1) rotate(5deg)",
    },
  });

  const callStats = [
    {
      title: "Total Calls",
      // value: callSummary?.data?.Total ?? 0,
      value: callsSummary?.data?.Total ?? 0,
      subValue: "Today",
      icon: CallIcon,
      accent: "#1976d2",
    },
    {
      title: "Answered Calls",
      // value: callSummary?.data?.Answered ?? 0,
      value: callsSummary?.data?.Answered ?? 0,
      subValue: "Successful",
      icon: CallReceivedIcon,
      accent: "#2e7d32",
    },
    {
      title: "Missed Calls",
      // values: {
      //   missed: callSummary?.data?.Missed ?? 0,
      // },
      value: callsSummary?.data?.Missed ?? 0,
      subValue: "Needs attention",
      icon: CallMissed, // ❌ call not received
      accent: "#f59e0b", // Amber / Warning
    },

    {
      title: "Failed Calls",
      // values: {
      //   failed: callSummary?.data?.Failed ?? 0,
      // },
      value: callsSummary?.data?.Failed ?? 0,
      subValue: "Needs attention",
      icon: CallEnd, // ❌ call failed
      accent: "#dc2626", // Red / Error
    },

  

    // Two old IVR cards (IVR Auth Success / IVR Auth Failed) consolidated
    // into the wider IvrAuthCard rendered separately in the JSX below.
    {
      title: "Total Unique Call",
      value: callsSummary?.data?.total_uniqe_call ?? 0,
      subValue: "Today",
      icon: CallIcon, // 📞 call icon
      accent: "#3b82f6", // blue (info)
    },
  ];

  const cardStyle1 = {
    p: 3,
    borderRadius: "16px",
    boxShadow: "0 8px 25px rgba(0,0,0,0.08)",
    transition: "0.3s",
    background: "#fff",
    "&:hover": {
      transform: "translateY(-6px)",
      boxShadow: "0 20px 40px rgba(0,0,0,0.15)",
    },
  };

  const progressStyle1 = {
    height: 8,
    borderRadius: 5,
    mt: 1,
  };

  const StatCard = ({ title, value, icon: Icon, accent }) => {
    return (
      <Card
        sx={{
          width: "100%",
          height: "100%",
          boxSizing: "border-box",
          // Padding scales so cards stay legible when zoomed (CSS Grid
          // forces N columns to share width, so each card shrinks).
          p: "clamp(0.55rem, 1.1vw, 1rem)",
          borderRadius: "20px",
          backgroundColor: "#fff",
          position: "relative",
          overflow: "hidden",
          boxShadow: `0 8px 32px ${accent}22`,
          border: `1px solid ${accent}22`,
          transition: "all 0.35s ease",
          cursor: "pointer",
          minWidth: 0,

          "&:hover": {
            transform: "translateY(-6px)",
            boxShadow: `0 20px 40px ${accent}33`,
          },

          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            width: "6px",
            height: "100%",
            backgroundColor: accent,
            borderTopLeftRadius: "20px",
            borderBottomLeftRadius: "20px",
          },
        }}
      >
        {/* Two-row layout: full-width title on top, then value + icon
            sharing the second row. Title gets dedicated horizontal space
            so it doesn't compete with the icon and stays readable when the
            card narrows. */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: "clamp(2px, 0.4vw, 6px)", minWidth: 0, position: "relative", zIndex: 1 }}>
          <Typography
            sx={{
              fontSize: "clamp(0.7rem, 0.95vw, 0.85rem)",
              fontWeight: 700,
              textTransform: "capitalize",
              color: accent,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              ".App.user_theme_white &": {
                color: "#0f172a!important",
              },
            }}
          >
            {title}
          </Typography>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "clamp(4px, 0.6vw, 10px)",
              minWidth: 0,
            }}
          >
            <Box sx={{ minWidth: 0, overflow: "hidden", flex: 1 }}>
              {value !== undefined && (
                <Typography
                  sx={{
                    fontSize: "clamp(1.1rem, 2.2vw, 1.9rem)",
                    fontWeight: 700,
                    color: "#1f2937",
                    lineHeight: 1.15,
                  }}
                >
                  {value}
                </Typography>
              )}
            </Box>

            {/* Icon — smaller max than before since title no longer
                competes for vertical space inside the row. */}
            <Box
              sx={{
                width: "clamp(32px, 3.2vw, 52px)",
                height: "clamp(32px, 3.2vw, 52px)",
                flexShrink: 0,
                borderRadius: "clamp(9px, 1.2vw, 14px)",
                backgroundColor: `${accent}15`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: `2px solid ${accent}30`,
              }}
            >
              <Icon sx={{ fontSize: "clamp(16px, 1.9vw, 23px)", color: accent }} />
            </Box>
          </Box>
        </Box>
      </Card>
    );
  };

  const StatCardSkeleton = () => {
    return (
      <Card
        sx={{
          width: "100%",
          height: "100%",
          boxSizing: "border-box",
          p: "clamp(0.55rem, 1.1vw, 1rem)",
          borderRadius: "20px",
          backgroundColor: "#fff",
          boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
          minWidth: 0,
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: "clamp(2px, 0.4vw, 6px)", minWidth: 0 }}>
          {/* Title row */}
          <Skeleton width="70%" height={16} />
          {/* Value + icon row */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "clamp(4px, 0.6vw, 10px)",
              minWidth: 0,
            }}
          >
            <Skeleton width="55%" height={32} sx={{ flex: "1 1 auto" }} />
            <Skeleton
              variant="rounded"
              sx={{
                width: "clamp(32px, 3.2vw, 52px)",
                height: "clamp(32px, 3.2vw, 52px)",
                flexShrink: 0,
              }}
            />
          </Box>
        </Box>
      </Card>
    );
  };

  const StatStateSkeleton = () => (
    <Grid container spacing={1}>
      <Grid item xs={12} md={12}>
        <Card sx={{ p: 2, height: "100%", borderRadius: 3 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <Skeleton variant="circular" width={30} height={30} />
            <Skeleton width={140} height={28} />
          </Box>

          <Box mt={2}>
            <Skeleton height={25} width="60%" />
            <Skeleton height={25} width="70%" />
            <Skeleton height={25} width="50%" />
          </Box>

          <Box mt={2}>
            <Skeleton height={20} width="40%" />
            <Skeleton
              variant="rectangular"
              height={8}
              sx={{ borderRadius: 5 }}
            />
          </Box>
        </Card>
      </Grid>
    </Grid>
  );

  return (
    <>
      <div className={`App ${colorThem} `}>
        <div className="contant_box">
          <Box
            className="right_sidebox mobile_top_pddng"
            component="main"
            sx={{
              flexGrow: 1,
              p: 3,
              width: { sm: `calc(100% - ${drawerWidth}px)` },
            }}
          >
            <Grid container spacing={2}>
              {/* Total Calls */}
              <Grid item xs={12} md={12}>
                <Box
                  className="d-flex justify-content-between flex-wrap mb-3"
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: { xs: 1.5, md: 0 },
                    alignItems: { xs: "flex-start", md: "center" },
                  }}
                >
                  {/* LEFT */}
                  <Box sx={{ width: { xs: "100%", md: "auto" } }}>
                    <Chip
                      label="Call Summary"
                      sx={{
                        px: 2,
                        py: 1,
                        fontWeight: 600,
                        background:
                          "linear-gradient(135deg, #0a140e 0%, #0a140e 100%)",
                        color: "#fff",
                        fontSize: "0.85rem",
                        letterSpacing: 0.5,
                        width: { xs: "100%", md: "auto" }, // 📱 full width
                        justifyContent: "center",
                      }}
                    />
                  </Box>

                  {/* RIGHT */}
                  <Box
                    sx={{
                      width: { xs: "100%", sm: "auto" },
                      mt: { xs: 1, sm: 0 },
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: { xs: "12px", sm: "13px" },
                        fontWeight: 600,
                        px: { xs: 1.5, sm: 2 },
                        py: 0.8,
                        borderRadius: "8px",
                        backgroundColor: "#1976d215",
                        color: "#0e397f",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 1,
                        position: "relative",
                        overflow: "hidden",
                        width: { xs: "100%", sm: "auto" },
                        justifyContent: {
                          xs: "center",
                          sm: "flex-start",
                        },
                        textAlign: "center",

                        ".App.user_theme_white &": {
                          backgroundColor: "#1976d269",
                        },

                        "&::before": {
                          content: '""',
                          position: "absolute",
                          left: 0,
                          top: 0,
                          height: "100%",
                          width: "4px",
                          backgroundColor: "#1976d2",
                          borderRadius: "4px",
                        },

                        animation: "shrinkPulse 2.8s ease-in-out infinite",

                        "@keyframes shrinkPulse": {
                          "0%": { transform: "scale(1)", opacity: 1 },
                          "50%": {
                            transform: "scale(0.97)",
                            opacity: 0.85,
                          },
                          "100%": { transform: "scale(1)", opacity: 1 },
                        },
                      }}
                    >
                      Note: Refresh Time 00:00 am to 11:59 pm.
                    </Typography>
                  </Box>
                </Box>{" "}
                {/* CSS Grid (not flex+wrap) so the row never breaks to
                    a second line when zoomed. minmax(0, 1fr) lets each
                    column shrink uniformly; StatCard's content is
                    clamp()-scaled so the icon + value stay legible at
                    smaller widths. overflow-x: auto is the safety net at
                    extreme zoom (175%+) — horizontal scroll instead of wrap. */}
                <Box
                  className="stat_cards_container"
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "repeat(2, minmax(0, 1fr))",
                      sm: "repeat(3, minmax(0, 1fr))",
                      md: "repeat(7, minmax(0, 1fr))",
                    },
                    gap: "10px",
                    alignItems: "stretch",
                    overflowX: "auto",
                  }}
                >
                  {loader ? (
                    <>
                      {Array.from({ length: 5 }).map((_, index) => (
                        <StatCardSkeleton key={`s-${index}`} />
                      ))}
                      {/* IVR card placeholder spans 2 columns to match the
                          loaded layout below — keeps the grid steady. */}
                      <Box sx={{ gridColumn: { md: "span 2" } }}>
                        <StatCardSkeleton />
                      </Box>
                    </>
                  ) : (
                    <>
                      {callStats.map((item, index) => (
                        <StatCard key={index} {...item} />
                      ))}
                      {/* Consolidated IVR Authentication card — sits as the
                          rightmost grid item, spans 2 columns at md+ so the
                          rate + sparkline + trend have room. Grid totals
                          stay at 7 column units (5 narrow + 2-wide). */}
                      <Box sx={{ gridColumn: { md: "span 2" }, minWidth: 0, display: "flex" }}>
                        <IvrAuthCard data={callsSummary?.data?.ivr_auth} />
                      </Box>
                    </>
                  )}
                </Box>
              </Grid>
            </Grid>

            {/* Server Monitoring (left) + Top Carriers Today (right) — paired
                row. ServerStats is opt-in via button click (psutil's CPU
                sampling blocks ~1s, kept off the auto-fetch path). TopCarriers
                reads from the same Redux store CarrierMetricsChart already
                populates, so no extra round-trip. Stacks on mobile. */}
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                gap: 2,
                alignItems: "stretch",
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0, display: "flex" }}>
                <ServerStatsCombined />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0, display: "flex" }}>
                <TopCarriersWidget />
              </Box>
            </Box>

            <Box sx={{ mt: 3 }}>
              <CarrierMetricsChart colorThem={colorThem} />
            </Box>

            {/* <div className="row mt-4">
              <div className="col-lg-6 ">
                <div className="pie-container">
                  <h4>Calls Minutes Summary</h4>
                  <PieChart />
                </div>
              </div>

          
              <div className="col-lg-6 ">
                <div className="pie-container">
                  <h4>Campaign Chart</h4>
                  <Adminbarchart />
                </div>
              </div>

              

              <div className="col-lg-12 mt-xxl-4 mt-xl-4 mt-lg-4 mt-md-4 mt-sm-0 mt-xs-0 mt-0">
                <div className="pie-container">
                  <h4>
                    Average Call Duration(ACD) and Answer-Seizure Ratio(ASR)
                    Trend Chart
                  </h4>
                  <LineChart />
                </div>
              </div>
            </div> */}
          </Box>
        </div>
      </div>
    </>
  );
}

export default AdminDashboard;
